import {
  DateTimePicker,
  Modal,
  PrimaryButton,
  setDateWithTimezone,
  toISOStringWithoutTimezone,
  toast,
} from '@systemeio/ui'
import { BlogPostId, BlogPostsInterface } from 'modules/blogs/types/blog-posts-interface'
import React, { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useSchedulePostApi } from '../hooks/use-schedule-post-api'

export interface SchedulePostModalProps {
  opened: boolean
  onClose: () => void
  post: BlogPostsInterface
  onSchedulePost: (postId: BlogPostId, data: BlogPostsInterface) => void
}

export const SchedulePostModal = ({
  opened,
  onClose,
  post,
  onSchedulePost,
}: SchedulePostModalProps) => {
  const { t } = useLocoTranslation()

  const { user, intlLocale } = useUser()

  const [dateTimePicker, setDateTimePicker] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const { schedulePost } = useSchedulePostApi()
  const onClosed = () => {
    onClose()
    setDateTimePicker(null)
  }

  useEffect(() => {
    if (opened && !dateTimePicker && user) {
      const date = setDateWithTimezone(undefined, user.timezone)
      date.setHours(date.getHours() + 1)
      setDateTimePicker(toISOStringWithoutTimezone(date))
    }
  }, [opened, dateTimePicker, user])

  const handleConfirm = async () => {
    setIsFetching(true)
    try {
      if (dateTimePicker) {
        const response = await schedulePost(post.id, { activatedAt: dateTimePicker })
        onSchedulePost(post.id, response)
      }
      onClosed()
      toast.success(t('dashboard.blog.posts.created'))
    } catch (e) {}
    setIsFetching(false)
  }

  return (
    <>
      <Modal
        isCentered
        opened={opened}
        onClose={onClosed}
        title={t('dashboard.blog.posts.schedule_modal_title')}
      >
        <form className="m-auto flex max-w-fit flex-col gap-6 lg:gap-10">
          <DateTimePicker
            key={'date'}
            value={dateTimePicker}
            onChange={date => {
              setDateTimePicker(date)
            }}
            createdAt={user?.createdAt}
            timezone={user?.timezone}
            intlLocale={intlLocale}
          />
          <div className="flex justify-center">
            <PrimaryButton
              isFetching={isFetching}
              disabled={!dateTimePicker}
              width="large"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                handleConfirm()
              }}
              type="submit"
            >
              {t('dashboard.blog.posts.save_and_schedule')}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </>
  )
}
